import getFartherManagedAccounts from "@fartherfinance/frontend/api/Accounts/requests/getFartherManagedAccounts";
import getManuallyTrackedAccounts from "@fartherfinance/frontend/api/Accounts/requests/getManuallyTrackedAccounts";
import {
  FartherManagedAccount,
  ManuallyTrackedAccount,
} from "@fartherfinance/frontend/api/Accounts/Types";
import getAllExternalAccounts from "@fartherfinance/frontend/api/ExternalAccount/requests/getAllExternalAccounts";
import { ExternalAccount } from "@fartherfinance/frontend/api/ExternalAccount/requests/getExternalAccounts";
import getFundingAccount from "@fartherfinance/frontend/api/ExternalAccount/requests/getFundingAccount";
import {
  AdvisorRequestConfig,
  ClientId,
} from "@fartherfinance/frontend/api/Types";

import {
  EnumOption,
  isResourceTypeWithValue,
  ResourceTypeResponse,
} from "../../core/types";

import { cache5Min } from "./cache";

interface BaseAccountData {
  accountNumber?: string;
  accountType?: string;
}

interface ACHAccountData extends BaseAccountData {
  name?: string;
  accountTitle?: string;
  title?: string;
}

function hasProperty<T, K extends string>(
  obj: T,
  prop: K
): obj is T & Record<K, unknown> {
  return obj !== null && typeof obj === "object" && prop in obj;
}
function hasKind(
  kindValue: string[] | string | undefined,
  targetKind: string
): boolean {
  if (!kindValue) {
    return false;
  }
  return Array.isArray(kindValue)
    ? kindValue.includes(targetKind)
    : kindValue === targetKind;
}
async function getClientExternalAccounts(
  clientId: ClientId,
  config: AdvisorRequestConfig
): Promise<ExternalAccount[]> {
  return cache5Min(`external-accounts-${clientId}`, async () => {
    try {
      const accounts = await getAllExternalAccounts(clientId, {
        clientId,
        jwt: config.jwt,
        advisorId: config.advisorId,
      });
      return accounts?.accounts || [];
    } catch (error) {
      console.error(
        "[ResourceResolver:Account] Error fetching external accounts:",
        error
      );
      return [];
    }
  });
}

// Function to fetch manually tracked accounts for a specific client
async function getClientManualAccounts(
  clientId: ClientId,
  config: AdvisorRequestConfig
): Promise<ManuallyTrackedAccount[]> {
  return cache5Min(`manual-accounts-${clientId}`, async () => {
    try {
      const accounts = await getManuallyTrackedAccounts(clientId, {
        jwt: config.jwt,
        clientId,
        advisorId: config.advisorId,
      });
      return accounts || [];
    } catch (error) {
      console.error(
        "[ResourceResolver:Account] Error fetching manual accounts:",
        error
      );
      return [];
    }
  });
}

// Function to fetch Farther managed accounts for a specific client
async function getClientFartherAccounts(
  clientId: ClientId,
  config: AdvisorRequestConfig
): Promise<FartherManagedAccount[]> {
  return cache5Min(`farther-accounts-${clientId}`, async () => {
    try {
      const accounts = await getFartherManagedAccounts(clientId, {
        jwt: config.jwt,
        clientId,
        advisorId: config.advisorId,
      });
      return accounts || [];
    } catch (error) {
      console.error(
        "[ResourceResolver:Account] Error fetching Farther accounts:",
        error
      );
      return [];
    }
  });
}

// Function to fetch funding accounts
async function getFundingAccounts(
  clientId: ClientId,
  config: AdvisorRequestConfig
): Promise<ExternalAccount[]> {
  return cache5Min("funding-accounts", async () => {
    try {
      const response = await getFundingAccount({
        jwt: config.jwt,
        clientId,
        advisorId: config.advisorId,
      });
      return response?.account ? [response.account] : [];
    } catch (error) {
      console.error(
        "[ResourceResolver:Account] Error fetching funding accounts:",
        error
      );
      return [];
    }
  });
}

function formatAccountLabel(
  accountNumber: string,
  accountName: string | undefined,
  accountType: string | undefined
): string {
  if (accountName) {
    return accountType
      ? `${accountName} - ${accountNumber} (${accountType})`
      : `${accountName} - ${accountNumber}`;
  }
  return accountType ? `${accountNumber} (${accountType})` : accountNumber;
}

export const fetchAccounts = async (
  requestConfig: AdvisorRequestConfig,
  values: ResourceTypeResponse[],
  params?: Record<string, unknown>
): Promise<EnumOption[]> => {
  if (
    !Array.isArray(values) ||
    !values.every((value) => isResourceTypeWithValue(value))
  ) {
    return [];
  }

  if (!params?.clientId) {
    console.warn("[ResourceResolver:Account] No client ID provided");
    return [];
  }

  const validClientId: ClientId =
    typeof params.clientId === "string"
      ? ClientId.parse(params.clientId)
      : ClientId.parse(String(params.clientId));
  const hasExternalKind = values.some(
    (v) => isResourceTypeWithValue(v) && hasKind(v.kind, "external")
  );

  const hasManualKind = values.some(
    (v) => isResourceTypeWithValue(v) && hasKind(v.kind, "manually-tracked")
  );

  const hasFartherKind = values.some(
    (v) => isResourceTypeWithValue(v) && hasKind(v.kind, "farther")
  );

  const hasFundingKind = values.some(
    (v) => isResourceTypeWithValue(v) && hasKind(v.kind, "funding")
  );

  let externalAccounts: ExternalAccount[] = [];
  let manualAccounts: ManuallyTrackedAccount[] = [];
  let fartherAccounts: FartherManagedAccount[] = [];
  let fundingAccounts: ExternalAccount[] = [];
  const fetchPromises: Promise<{
    type: string;
    data:
      | ExternalAccount[]
      | ManuallyTrackedAccount[]
      | FartherManagedAccount[];
  }>[] = [];

  if (hasExternalKind) {
    fetchPromises.push(
      getClientExternalAccounts(validClientId, requestConfig).then(
        (data) => ({
          type: "external",
          data: data || [],
        }),
        (error) => {
          console.error(
            "[ResourceResolver:Account] Error fetching external accounts:",
            error
          );
          return { type: "external", data: [] };
        }
      )
    );
  }

  if (hasManualKind) {
    try {
      const clientIdForManual = ClientId.parse(validClientId);
      fetchPromises.push(
        getClientManualAccounts(clientIdForManual, requestConfig).then(
          (data) => ({
            type: "manual",
            data: data || [],
          }),
          (error) => {
            console.error(
              "[ResourceResolver:Account] Error fetching manual accounts:",
              error
            );
            return { type: "manual", data: [] };
          }
        )
      );
    } catch (error) {
      console.error(
        "[ResourceResolver:Account] Invalid client ID for manual accounts:",
        error
      );
    }
  }

  if (hasFartherKind) {
    try {
      const clientIdForFarther = ClientId.parse(validClientId);
      fetchPromises.push(
        getClientFartherAccounts(clientIdForFarther, requestConfig).then(
          (data) => ({
            type: "farther",
            data: data || [],
          }),
          (error) => {
            console.error(
              "[ResourceResolver:Account] Error fetching Farther accounts:",
              error
            );
            return { type: "farther", data: [] };
          }
        )
      );
    } catch (error) {
      console.error(
        "[ResourceResolver:Account] Invalid client ID for Farther accounts:",
        error
      );
    }
  }

  if (hasFundingKind) {
    fetchPromises.push(
      getFundingAccounts(validClientId, requestConfig).then(
        (data) => ({
          type: "funding",
          data: data || [],
        }),
        (error) => {
          console.error(
            "[ResourceResolver:Account] Error fetching funding accounts:",
            error
          );
          return { type: "funding", data: [] };
        }
      )
    );
  }

  // Wait for all data to be fetched with graceful error handling
  const results = await Promise.allSettled(fetchPromises);

  // Process results and assign to appropriate variables
  results.forEach((result) => {
    if (result.status === "fulfilled") {
      const { type, data } = result.value;
      switch (type) {
        case "external":
          externalAccounts = data as ExternalAccount[];
          break;
        case "manual":
          manualAccounts = data as ManuallyTrackedAccount[];
          break;
        case "farther":
          fartherAccounts = data as FartherManagedAccount[];
          break;
        case "funding":
          fundingAccounts = data as ExternalAccount[];
          break;
      }
    } else {
      console.error(
        "[ResourceResolver:Account] Promise rejected:",
        result.reason
      );
    }
  });

  // Process resources and map to options
  const options: EnumOption[] = [];

  for (const resource of values.filter(isResourceTypeWithValue)) {
    const resourceKind = Array.isArray(resource.kind)
      ? resource.kind
      : resource.kind
      ? [resource.kind as string]
      : [];

    // Handle external accounts
    if (resourceKind.includes("external")) {
      const account = externalAccounts.find(
        (a) => a.accountId === resource.value
      );
      if (account) {
        // Default values from account ID
        let accountNumber = String(account.accountId);
        let accountName: string | undefined = undefined;
        let accountType: string | undefined = undefined;

        // Check if this is a Plaid account with details
        if (hasProperty(account, "details") && account.details) {
          // If it has an account property, extract the data with proper typing
          if (
            hasProperty(account.details, "account") &&
            account.details.account
          ) {
            const accountDetails = account.details.account as ACHAccountData;

            // Extract data using the typed interface
            if (accountDetails.accountNumber) {
              accountNumber = accountDetails.accountNumber;
            }

            // Get the name using multiple possible field names
            accountName = accountDetails.name || accountDetails.accountTitle;

            // Get the account type
            accountType = accountDetails.accountType;
          }
        }

        options.push({
          label: formatAccountLabel(accountNumber, accountName, accountType),
          value: String(resource.value),
          type: resource.type,
          kind: resource.kind,
        });
      }
    }

    // Handle manually tracked accounts
    if (resourceKind.includes("manually-tracked")) {
      const account = manualAccounts.find((a) => a.id === resource.value);
      if (account) {
        // Manual accounts have a specific structure
        const accountNumber = String(account.id);
        const accountName = account.assetName;
        const accountType = account.assetType;

        options.push({
          label: formatAccountLabel(accountNumber, accountName, accountType),
          value: String(resource.value),
          type: resource.type,
          kind: resource.kind,
        });
      }
    }

    // Handle Farther managed accounts
    if (resourceKind.includes("farther")) {
      const account = fartherAccounts.find(
        (a) => a.virtualAccountId === resource.value
      );
      if (account) {
        // Farther accounts have a specific structure
        const accountNumber = String(account.virtualAccountId);
        const accountName = account.accountDetails?.displayName || "";
        const accountType = account.accountDetails?.accountType || "";

        options.push({
          label: formatAccountLabel(accountNumber, accountName, accountType),
          value: String(resource.value),
          type: resource.type,
          kind: resource.kind,
        });
      }
    }

    // Handle funding accounts
    if (resourceKind.includes("funding")) {
      const account = fundingAccounts.find(
        (a) => a.accountId === resource.value
      );
      if (account) {
        // Default values from account ID
        let accountNumber = String(account.accountId);
        let accountName: string | undefined = undefined;
        let accountType: string | undefined = undefined;

        // Check if this is a Plaid account with details
        if (hasProperty(account, "details") && account.details) {
          // If it has an account property, extract the data with proper typing
          if (
            hasProperty(account.details, "account") &&
            account.details.account
          ) {
            const accountDetails = account.details.account as ACHAccountData;

            // Extract data using the typed interface
            if (accountDetails.accountNumber) {
              accountNumber = accountDetails.accountNumber;
            }

            // Get the name using multiple possible field names for funding accounts
            accountName =
              accountDetails.title ||
              accountDetails.accountTitle ||
              accountDetails.name;

            // Get the account type
            accountType = accountDetails.accountType;
          }
        }

        options.push({
          label: formatAccountLabel(accountNumber, accountName, accountType),
          value: String(resource.value),
          type: resource.type,
          kind: resource.kind,
        });
      }
    }
  }

  return options;
};
